import { useEffect, useState } from 'react';

import api from '../services/api';
import { useAuth } from './auth';

export type RegimeEnum = 'simples_nacional' | 'lucro_presumido' | 'lucro_real';

interface RegimeTributarioLookup {
  id: number;
  descricao: string;
  atac_notas_enum: RegimeEnum;
}

interface UseRegimeTributarioResult {
  regime: RegimeEnum | null;
  loading: boolean;
}

/**
 * Resolve o regime tributário da empresa logada.
 *
 * Não existe endpoint que devolva o regime já resolvido, nem o `user` do
 * useAuth carrega essa informação: é preciso ler o `regime_tributario_id` da
 * empresa e cruzar com a tabela de regimes para chegar no enum. Mesmo caminho
 * que a aba Fiscal (pages/Configuracoes/TabBar/tabs/Fiscal.tsx) já faz.
 *
 * `regime` fica null quando a empresa não tem regime cadastrado ou a busca
 * falha — nesse caso quem consome deve degradar para "mostrar tudo", nunca
 * assumir um regime.
 */
export function useRegimeTributario(): UseRegimeTributarioResult {
  const { user } = useAuth();

  const [regime, setRegime] = useState<RegimeEnum | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    // Ao trocar de empresa sem remontar, o regime anterior não vale mais: sem
    // isto o select ofereceria a tabela da empresa antiga até a nova resposta.
    setLoading(true);
    setRegime(null);

    async function loadRegime(): Promise<void> {
      try {
        const [{ data: empresa }, { data: regimes }] = await Promise.all([
          api.get(`/empresa/${user.empresa_id}`),
          api.get('/regimes-tributarios'),
        ]);

        if (!active) return;

        // Number() nos dois lados: a entidade Empresa declara a coluna como
        // `numeric` (o DDL é int4), e um driver que respeite a declaração
        // devolveria string — a comparação estrita falharia em silêncio.
        const found = (regimes as RegimeTributarioLookup[]).find(
          (item) => Number(item.id) === Number(empresa.regime_tributario_id),
        );

        setRegime(found?.atac_notas_enum ?? null);
      } catch (err) {
        // Falhar aqui é silencioso por natureza: a tela continua funcionando,
        // só passa a oferecer as duas tabelas de CST. Registra para não sumir.
        console.error('Falha ao carregar o regime tributário da empresa', err);
        if (active) setRegime(null);
      } finally {
        if (active) setLoading(false);
      }
    }

    loadRegime();

    return () => {
      active = false;
    };
  }, [user.empresa_id]);

  return { regime, loading };
}

export default useRegimeTributario;
